import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { messageTemplatesService } from '@/services/messageTemplates.service';
import type { TemplateDTO, TemplateSortParams, UpdateTemplatePayload } from '@/types/messageTemplates';

export const useMessageTemplateTags = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.MESSAGE_TEMPLATE_TAGS],
    queryFn: () => messageTemplatesService.getTags(),
  });
};

export const useMessageTemplates = (params?: { sort?: TemplateSortParams; cursor?: string; limit?: number }) => {
  return useQuery({
    queryKey: [QUERY_KEYS.MESSAGE_TEMPLATES, params],
    queryFn: () => messageTemplatesService.getTemplates(params),
  });
};

export const useMessageTemplateById = (id: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.MESSAGE_TEMPLATE_BY_ID, id],
    queryFn: () => messageTemplatesService.getTemplateById(id ?? ''),
    enabled: !!id,
  });
};

export const useMessageTemplateTagsById = (id: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.MESSAGE_TEMPLATE_TAGS, id],
    queryFn: () => messageTemplatesService.getTemplateTags(id ?? ''),
    enabled: !!id,
  });
};

export const useSaveMessageTemplateDraftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TemplateDTO) => messageTemplatesService.saveDraft(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATES] });
    },
  });
};

export const useUpdateMessageTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTemplatePayload }) =>
      messageTemplatesService.updateTemplate(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATE_BY_ID] });
    },
  });
};
